import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Droplets, MapPin, ArrowRight, Plus, LogOut, Calendar, ShieldCheck, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { BrandLogo } from "@/components/BrandLogo";
import { HealthScoreGauge } from "@/components/passport/HealthScoreGauge";

export default function PassportDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/passport").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <BrandLogo to="/" size="sm" />
          <div className="flex items-center gap-3">
            <Link to="/customer" className="text-sm font-medium text-slate-600 hover:text-slate-900">My account</Link>
            <button onClick={async () => { await logout(); window.location.assign("/"); }} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
          <ShieldCheck className="w-3.5 h-3.5" /> AWARE Water Health Record
        </div>
        <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">My properties</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">Your permanent water-quality record across every property you own. Each test contributes to your long-term Water Health Score™.</p>

        {loading ? (
          <div className="mt-10 text-slate-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="mt-10 bg-white rounded-3xl border border-slate-200 p-10 text-center">
            <Droplets className="w-10 h-10 text-cyan-500 mx-auto mb-3" />
            <h3 className="font-display font-semibold text-slate-900 text-lg">No properties yet</h3>
            <p className="text-slate-500 mt-2 max-w-md mx-auto">Book your first water test and your AWARE Water Health Record will populate automatically.</p>
            <Link to="/book" className="btn-aqua mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold">Book a test <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-2 gap-5">
            {items.map(({ property, latest_test, next_retest, pending_count }) => (
              <Link
                to={`/passport/property/${property.id}`}
                key={property.id}
                data-testid={`passport-property-${property.id}`}
                className="group bg-white rounded-3xl border border-slate-200 p-7 hover:shadow-xl hover:border-cyan-200 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start justify-between gap-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-cyan-600 font-semibold uppercase tracking-wider">
                      <MapPin className="w-3.5 h-3.5" /> {property.property_type}
                    </div>
                    <h3 className="font-display mt-2 text-xl font-semibold text-slate-900 leading-tight">
                      {property.nickname || property.address}
                    </h3>
                    {property.nickname && <p className="text-xs text-slate-500 mt-1">{property.address}</p>}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(property.sources || []).map((s) => (
                        <span key={s.id} className="px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-[11px] font-medium capitalize">
                          {s.type.replace("_", " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                  {latest_test && <HealthScoreGauge score={latest_test.health_score} band={latest_test.band} size={120} />}
                </div>
                <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Last tested</div>
                    <div className="text-slate-900 font-medium mt-0.5">
                      {latest_test ? formatDateAU(latest_test.collected_at) : (pending_count > 0 ? "Being processed" : "—")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Next retest due</div>
                    <div className="text-slate-900 font-medium mt-0.5 inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-cyan-500" />
                      {next_retest ? formatDateAU(next_retest.next_due_at) : "—"}
                    </div>
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-600 group-hover:translate-x-1 transition-transform">
                  View Passport <ExternalLink className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          <Link to="/book" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
            <Plus className="w-4 h-4" /> Book another test
          </Link>
          <Link to="/customer" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-white border border-slate-200 text-slate-900 text-sm font-semibold hover:border-slate-300">
            Back to my account
          </Link>
        </div>
      </main>
    </div>
  );
}
